import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Calendar, Clock, Users, MapPin, Bookmark, ChevronLeft, Shield, Check, X, AlertCircle, Info } from 'lucide-react';
import Layout from '../Layout';
import { WorkshopManager } from '../../helpers/workshop';
import { checkLoggedIn, getCurrentUserData } from '../../helpers/user';
import { createPaymentLink } from '../../helpers/stripe';
import { showToast } from '../../helpers/customToast';
import { API_BASE_URL } from '../../helpers/ApiSettings';
import '../../css/ateliers.css';

const WorkshopDateItem = ({ date, selected, onSelect, availability }) => {
  const formatDate = (dateString) => {
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString('fr-FR', options);
  };
  
  const isAvailable = availability?.available !== false;
  const isPast = new Date(date.date) < new Date();
  
  return (
    <div 
      className={`workshop-date-item card mb-2 ${selected ? 'border-primary' : ''} ${!isAvailable || isPast ? 'opacity-50' : ''}`}
      onClick={() => {
        if (isAvailable && !isPast) {
          onSelect(date.documentId);
        }
      }}
      style={{ cursor: isAvailable && !isPast ? 'pointer' : 'not-allowed' }}
    >
      <div className="card-body d-flex justify-content-between align-items-center">
        <div className="workshop-date-info">
          <div className="d-flex align-items-center">
            <Calendar size={18} className="me-2 text-primary" />
            <span className="fw-bold">{formatDate(date.date)}</span>
          </div>
          
          {/* {availability && (
            <div className="text-muted small mt-1">
              {availability.available ? (
                <span>{availability.remainingSpots} place(s) disponible(s)</span>
              ) : (
                <span className="text-danger">Complet</span>
              )}
            </div>
          )} */}
        </div>
        
        {selected ? (
          <Check className="text-primary" size={24} />
        ) : isPast ? (
          <span className="badge bg-secondary">Passé</span>
        ) : !isAvailable ? (
          <span className="badge bg-danger">Complet</span>
        ) : null}
      </div>
    </div>
  );
};

const WorkshopDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [workshop, setWorkshop] = useState(null);
  const [dates, setDates] = useState([]);
  const [selectedDateId, setSelectedDateId] = useState(null);
  const [dateAvailabilities, setDateAvailabilities] = useState({});
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUserData, setCurrentUserData] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  
  useEffect(() => {
    const initialize = async () => {
      try {
        setLoading(true);
        
        // Check authentication
        const authStatus = await checkLoggedIn();
        setIsAuthenticated(authStatus);
        
        if (authStatus) {
          const userData = await getCurrentUserData();
          setCurrentUserData(userData);
        }
        
        // Load workshop data
        const workshopData = await WorkshopManager.fetchWorkshopById(id);
        setWorkshop(workshopData);
        
        // Load workshop dates
        const datesData = await WorkshopManager.fetchWorkshopDates(id);
        setDates(datesData);
        
        // Check availability for each date
        const availabilities = {};
        for (const date of datesData) {
          const availability = await WorkshopManager.checkAvailability(date.documentId);
          availabilities[date.documentId] = availability;
        }
        setDateAvailabilities(availabilities);
        
      } catch (error) {
        console.error('Error initializing workshop page:', error);
        showToast('Erreur lors du chargement des données', 'error');
      } finally {
        setLoading(false);
      }
    };
    
    initialize();
  }, [id]);
  
  const handleDateSelect = (dateId) => {
    setSelectedDateId(dateId);
  };
  
  const handleReservation = async () => {
    if (!selectedDateId) {
      showToast('Veuillez sélectionner une date', 'error');
      return;
    }
    
    try {
      setIsProcessing(true);
      
      if (!isAuthenticated) {
        // Save selection and redirect to login
        sessionStorage.setItem('workshop_reservation', JSON.stringify({
          workshopId: workshop.id,
          dateId: selectedDateId
        }));
        
        navigate('/connexion?redirect=workshop');
        return;
      }
      
      // Check availability again
      const availability = await WorkshopManager.checkAvailability(selectedDateId);
      if (!availability.available) {
        showToast('Cette session est complète, veuillez en choisir une autre', 'error');
        // Refresh availabilities
        const newAvailabilities = { ...dateAvailabilities };
        newAvailabilities[selectedDateId] = availability;
        setDateAvailabilities(newAvailabilities);
        return;
      }
      
      // Create reservation
      const reservationData = {
        userId: currentUserData.id,
        workshopId: workshop.id,
        dateId: selectedDateId,
        totalPrice: workshop.price,
        userDetails: {
          first_name: currentUserData.first_name || '',
          last_name: currentUserData.last_name || '',
          email: currentUserData.email || '',
          phone: currentUserData.user_address?.phone || ''
        }
      };
      
      const reservationResult = await WorkshopManager.createReservation(reservationData);

      if (!reservationResult.success) {
        throw new Error(reservationResult.error || 'Échec de la réservation');
      }

      // If workshop is free (price is 0), skip payment and go directly to confirmation
      if (workshop.price === 0) {
        // Update status to confirmed for free workshops
        await WorkshopManager.updateReservation(reservationResult.documentId, {
          resa_status: 'confirmed'
        });
        navigate(`/atelier-confirmation?document_id=${reservationResult.documentId}&free=true`);
        return;
      }

      // Create payment link for paid workshops
      const paymentLinkResult = await createPaymentLink({
        orderId: reservationResult.reservationId,
        documentId: reservationResult.documentId,
        totalAmount: workshop.price,
        customerEmail: currentUserData.email,
        firstName: currentUserData.first_name,
        lastName: currentUserData.last_name,
        orderType: 'workshop'
      });

      // Redirect to payment
      window.location.href = paymentLinkResult.url;
      
    } catch (error) {
      console.error('Error processing reservation:', error);
      showToast(error.message || 'Une erreur est survenue, veuillez réessayer', 'error');
    } finally {
      setIsProcessing(false);
    }
  };
  
  if (loading) {
    return (
      <Layout>
        <div className="container my-5 text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Chargement...</span>
          </div>
          <p className="mt-3">Chargement des détails de l'atelier...</p>
        </div>
      </Layout>
    );
  }
  
  if (!workshop) {
    return (
      <Layout>
        <div className="container my-5">
          <div className="alert alert-danger">
            Atelier non trouvé
          </div>
          <Link to="/ateliers" className="btn btn-primary">
            <ChevronLeft size={18} className="me-2" />
            Retour aux ateliers
          </Link>
        </div>
      </Layout>
    );
  }
  
  const futureDates = dates.filter(date => new Date(date.date) > new Date());
  
  return (
    <Layout>
      <div className="container my-5">
        <div className="mb-4">
          <Link to="/ateliers" className="btn btn-outline-primary">
            <ChevronLeft size={18} className="me-2" />
            Retour aux ateliers
          </Link>
        </div>
        
        <div className="row">
          <div className="col-lg-8">
            <div className="card mb-4">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <h1 className="card-title h2">{workshop.title}</h1>
                  <span className="badge bg-primary">{workshop.reference}</span>
                </div>
                
                {workshop.image && (
                  <img 
                    src={`${API_BASE_URL}${workshop.image.url}`} 
                    className="img-fluid rounded mb-4" 
                    alt={workshop.title} 
                  />
                )}
                
                <div className="mb-4">
                  <div className="d-flex align-items-center text-muted mb-2">
                    <Clock size={18} className="me-2" />
                    <span>Durée: {workshop.duration} minutes</span>
                  </div>
                  
                  {/* <div className="d-flex align-items-center text-muted mb-2">
                    <Users size={18} className="me-2" />
                    <span>Max: {workshop.max_participants || 12} participants</span>
                  </div> */}
                  
                  {workshop.location && (
                    <div className="d-flex align-items-center text-muted mb-2">
                      <MapPin size={18} className="me-2" />
                      <span>{workshop.location}</span>
                    </div>
                  )}
                  
                  <div className="d-flex align-items-center text-muted">
                    <Bookmark size={18} className="me-2" />
                    <span>Catégorie: {workshop.category?.name || 'Non catégorisé'}</span>
                  </div>
                </div>
                
                <div className="workshop-description mb-4">
                  <h3>Description</h3>
                  <div dangerouslySetInnerHTML={{ __html: workshop.description }} />
                </div>
              </div>
            </div>
          </div>
          
          <div className="col-lg-4">
            <div className="card mb-4">
              <div className="card-header bg-primary text-white">
                <h3 className="card-title h5 mb-0 text-white">Réserver votre place</h3>
              </div>
              <div className="card-body">
                <div className="price-tag mb-4">
                  <span className="fs-2 fw-bold text-primary">
                    {workshop.price === 0 ? 'Gratuit' : workshop.price ? workshop.price.toFixed(2)+'€' : 'Prix non communiqué'}
                  </span>
                  {workshop.price > 0 && (
                    <span className="text-muted"> / personne</span>
                  )}
                </div>
                
                {futureDates.length === 0 ? (
                  <div className="alert alert-warning">
                    <AlertCircle size={18} className="me-2" />
                    Aucune date disponible pour le moment
                  </div>
                ) : (
                  <>
                    <h4 className="mb-3">Sélectionnez une date</h4>
                    <div className="workshop-dates-container mb-4">
                      {futureDates.map(date => (
                        <WorkshopDateItem
                          key={date.documentId}
                          date={date}
                          selected={selectedDateId === date.documentId}
                          onSelect={handleDateSelect}
                          availability={dateAvailabilities[date.documentId]}
                        />
                      ))}
                    </div>
                    
                    <button
                      className="btn btn-primary btn-lg w-100"
                      disabled={!selectedDateId || isProcessing || workshop.price === null || workshop.price === undefined}
                      onClick={handleReservation}
                    >
                      {isProcessing ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>
                          Traitement en cours...
                        </>
                      ) : (
                        'Réserver maintenant'
                      )}
                    </button>
                    
                    {!isAuthenticated && (
                      <div className="alert alert-info mt-3 mb-0">
                        <Info size={16} className="me-2" />
                        Vous devrez vous connecter ou créer un compte pour finaliser votre réservation
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
            
            <div className="card mb-4">
              <div className="card-body">
                <h4>Informations importantes</h4>
                <ul className="list-group list-group-flush">
                  <li className="list-group-item d-flex">
                    <Check size={18} className="text-success me-2 flex-shrink-0" />
                    <span>Animé par des professionnels qualifiés</span>
                  </li>
                  {/* <li className="list-group-item d-flex">
                    <Check size={18} className="text-success me-2 flex-shrink-0" />
                    <span>Groupe limité à {workshop.max_participants || 12} participants maximum</span>
                  </li> */}
                  <li className="list-group-item d-flex">
                    <Check size={18} className="text-success me-2 flex-shrink-0" />
                    <span>Support pédagogique inclus</span>
                  </li>
                  <li className="list-group-item d-flex">
                    <Check size={18} className="text-success me-2 flex-shrink-0" />
                    <span>Suivi pendant 20 jours après l'atelier</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default WorkshopDetailPage;